'use client';

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IMG } from '../../../imgPrefix';

const FALLBACK_IMAGE = `${IMG}/image/no-image.png`;
const YOUTUBE_FALLBACK_IMAGE = `${IMG}/image/you-tube-bg.jpg`;

/** Extracts the 11-char video id from any real LTD `MultimediaContent[].Url`
 *  shape seen live (`youtu.be/{id}`, `youtube.com/watch?v={id}`,
 *  `youtube.com/embed/{id}`). Returns `null` for anything else so callers
 *  can fall back to the placeholder instead of embedding a broken player. */
function parseYoutubeId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) return u.pathname.split('/').filter(Boolean)[0] || null;
    if (u.hostname.includes('youtube.com')) {
      if (u.searchParams.get('v')) return u.searchParams.get('v');
      const segments = u.pathname.split('/').filter(Boolean);
      return segments[segments.length - 1] || null;
    }
    return null;
  } catch {
    return null;
  }
}

interface MediaItem {
  type: 'image' | 'video';
  src: string;
  /** Thumbnail/poster for a video item. */
  poster?: string;
}

interface MediaGalleryProps {
  primaryImage: string | null;
  secondaryImages: string[];
  trailerUrl: string | null;
}

export function MediaGallery({ primaryImage, secondaryImages, trailerUrl }: MediaGalleryProps) {
  const [viewer, setViewer] = useState<{ open: boolean; index: number }>({ open: false, index: 0 });
  const [zoomed, setZoomed] = useState(false);
  const [playing, setPlaying] = useState(false);

  const [loadedMap, setLoadedMap] = useState<Record<string, boolean>>({});

  const markLoaded = (src: string) => {
    setLoadedMap((prev) => (prev[src] ? prev : { ...prev, [src]: true }));
  };

  const isLoaded = (src?: string | null) => {
    if (!src) return true;
    return !!loadedMap[src];
  };

  // Use the fallback image as the YouTube trailer thumbnail poster
  const trailerPoster = YOUTUBE_FALLBACK_IMAGE;

  const trailerItem: MediaItem | null = trailerUrl
    ? { type: 'video', src: trailerUrl, poster: trailerPoster }
    : null;

  const media: MediaItem[] = [];
  let mainSrc: string | null = null;
  let mainPoster: string | null = null;
  let mainIsVideo = false;
  const sideItems: MediaItem[] = [];

  if (primaryImage) {
    mainSrc = primaryImage;
    media.push({ type: 'image', src: primaryImage });
    if (secondaryImages[0]) media.push({ type: 'image', src: secondaryImages[0] });
    if (trailerItem) media.push(trailerItem);
    secondaryImages.slice(1).forEach((img) => media.push({ type: 'image', src: img }));

    if (secondaryImages[0]) sideItems.push({ type: 'image', src: secondaryImages[0] });
    if (trailerItem) sideItems.push(trailerItem);
    else if (secondaryImages[1]) sideItems.push({ type: 'image', src: secondaryImages[1] });
  } else if (secondaryImages.length > 0) {
    mainSrc = secondaryImages[0];
    media.push({ type: 'image', src: secondaryImages[0] });
    if (secondaryImages[1]) media.push({ type: 'image', src: secondaryImages[1] });
    if (trailerItem) media.push(trailerItem);
    secondaryImages.slice(2).forEach((img) => media.push({ type: 'image', src: img }));

    if (secondaryImages[1]) sideItems.push({ type: 'image', src: secondaryImages[1] });
    if (trailerItem) sideItems.push(trailerItem);
    else if (secondaryImages[2]) sideItems.push({ type: 'image', src: secondaryImages[2] });
  } else if (trailerItem) {
    mainSrc = trailerItem.src;
    mainPoster = trailerItem.poster ?? null;
    mainIsVideo = true;
    media.push(trailerItem);
  }

  const cappedSideItems = sideItems.slice(0, 2);
  const showViewAll = media.length > 3;
  const hasSide = cappedSideItems.length > 0;
  const lastImageIndex = cappedSideItems.map((item) => item.type).lastIndexOf('image');

  function openViewer(src: string, isVideo = false) {
    const index = media.findIndex((m) => m.src === src && (isVideo ? m.type === 'video' : m.type === 'image'));
    const finalIndex = index >= 0 ? index : media.findIndex((m) => m.src === src);
    setViewer({ open: true, index: finalIndex >= 0 ? finalIndex : 0 });
    setZoomed(false);
    setPlaying(isVideo);
  }

  function closeViewer() {
    setViewer({ open: false, index: 0 });
    setPlaying(false);
  }

  function step(delta: number) {
    setViewer((v) => ({ open: true, index: (v.index + delta + media.length) % media.length }));
    setZoomed(false);
    setPlaying(false);
  }

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = viewer.open ? 'hidden' : '';
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
    };
  }, [viewer.open]);

  const current = media[viewer.index];
  const mainImgUrl = mainIsVideo ? mainPoster ?? YOUTUBE_FALLBACK_IMAGE : mainSrc;
  const mainLoaded = isLoaded(mainImgUrl);

  return (
    <div className={`shows-detail-gallery-row ${!hasSide ? 'shows-detail-gallery-row--single' : ''}`}>
      <div className="shows-detail-gallery-main">
        {mainSrc ? (
          <button
            type="button"
            className={`shows-detail-gallery-item ${!mainLoaded ? 'shows-gallery-shimmer' : ''}`}
            onClick={() => openViewer(mainSrc!, mainIsVideo)}
          >
            <img
              src={mainImgUrl ?? undefined}
              alt=""
              className={`shows-gallery-img ${mainLoaded ? 'shows-gallery-img-loaded' : 'shows-gallery-img-loading'}`}
              ref={(el) => {
                if (el && el.complete && el.naturalWidth !== 0 && mainImgUrl) {
                  markLoaded(mainImgUrl);
                }
              }}
              onLoad={() => {
                if (mainImgUrl) markLoaded(mainImgUrl);
              }}
              onError={(e) => {
                const fallback = mainIsVideo ? YOUTUBE_FALLBACK_IMAGE : FALLBACK_IMAGE;
                if (e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
                if (mainImgUrl) markLoaded(mainImgUrl);
              }}
            />
            {mainIsVideo && (
              <span className="shows-detail-watch-trailer">
                <i className="fa-solid fa-circle-play" aria-hidden /> Watch Trailer
              </span>
            )}
          </button>
        ) : (
          <div className="shows-detail-gallery-item shows-detail-gallery-item--placeholder">
            <img src={FALLBACK_IMAGE} alt="No image available" />
          </div>
        )}
      </div>

      {hasSide && (
        <div className={`shows-detail-gallery-side ${cappedSideItems.length === 1 ? 'shows-detail-gallery-side--single' : ''}`}>
          {cappedSideItems.map((item, i) => {
            const sideSrc = item.type === 'video' ? item.poster ?? YOUTUBE_FALLBACK_IMAGE : item.src;
            const sideLoaded = isLoaded(sideSrc);
            return (
              <button
                type="button"
                key={`${item.type}-${item.src}-${i}`}
                className={`shows-detail-gallery-item ${!sideLoaded ? 'shows-gallery-shimmer' : ''}`}
                onClick={() => openViewer(item.src, item.type === 'video')}
              >
                <img
                  src={sideSrc}
                  alt=""
                  className={`shows-gallery-img ${sideLoaded ? 'shows-gallery-img-loaded' : 'shows-gallery-img-loading'}`}
                  ref={(el) => {
                    if (el && el.complete && el.naturalWidth !== 0) {
                      markLoaded(sideSrc);
                    }
                  }}
                  onLoad={() => markLoaded(sideSrc)}
                  onError={(e) => {
                    const fallback = item.type === 'video' ? YOUTUBE_FALLBACK_IMAGE : FALLBACK_IMAGE;
                    if (e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
                    markLoaded(sideSrc);
                  }}
                />
                {item.type === 'image' && showViewAll && i === lastImageIndex && (
                  <span className="shows-detail-gallery-viewall">View all</span>
                )}
                {item.type === 'video' && (
                  <span className="shows-detail-watch-trailer shows-detail-watch-trailer--small">
                    <i className="fa-solid fa-circle-play" aria-hidden /> Watch Trailer
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {viewer.open && current && typeof document !== 'undefined' && createPortal(
        <div className="shows-detail-lightbox" role="dialog" aria-modal="true">
          <button type="button" className="shows-detail-lightbox-close" onClick={closeViewer} aria-label="Close">
            <i className="fa-solid fa-xmark" aria-hidden />
          </button>

          {media.length > 1 && (
            <button type="button" className="shows-detail-lightbox-nav shows-detail-lightbox-nav--prev" onClick={() => step(-1)} aria-label="Previous">
              <i className="fa-solid fa-chevron-left" aria-hidden />
            </button>
          )}

          <div className="shows-detail-lightbox-stage">
            {current.type === 'image' ? (
              <img
                src={current.src}
                alt=""
                className={`shows-detail-lightbox-image ${zoomed ? 'is-zoomed' : ''}`}
                onClick={() => setZoomed((z) => !z)}
                onError={(e) => {
                  if (e.currentTarget.src !== FALLBACK_IMAGE) e.currentTarget.src = FALLBACK_IMAGE;
                }}
              />
            ) : playing && parseYoutubeId(current.src) ? (
              <div className="shows-detail-lightbox-video">
                <iframe
                  src={`https://www.youtube.com/embed/${parseYoutubeId(current.src)}?autoplay=1`}
                  title="Trailer"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  className="shows-detail-lightbox-video-el"
                />
              </div>
            ) : (
              <div className="shows-detail-lightbox-video">
                <img src={current.poster ?? YOUTUBE_FALLBACK_IMAGE} alt="" />
                <button type="button" className="shows-detail-lightbox-playbtn" onClick={() => setPlaying(true)} aria-label="Play trailer">
                  <i className="fa-solid fa-circle-play" aria-hidden />
                </button>
              </div>
            )}
          </div>

          {media.length > 1 && (
            <button type="button" className="shows-detail-lightbox-nav shows-detail-lightbox-nav--next" onClick={() => step(1)} aria-label="Next">
              <i className="fa-solid fa-chevron-right" aria-hidden />
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}